import {
  AccessGroupsByObjectId,
  AccessObjectSummary,
  AccessUsersByObjectId,
  ClassificationGroupedItem,
  ClassificationObject,
  ClassificationsGroupedParams,
  ClassificationsPrint,
  ClassificationTopWidget,
  ClassifiedObjectsParams,
  FileListResponseType,
  ObjectsByDataSourceParams,
  ObjectSummary
} from './classificationsSlice'
import { AnalyticsItem } from './classificationsAnalytics'
import { ENTITY_DETAILS_LIMIT } from './constants'
import {
  DATA_SOURCE_TYPES,
  FILTER_LAST_MODIFIED_TIME_KEY,
  LABELS_KEY,
  LIMIT_DEFAULT,
  MAIL_DATASOURCES,
  mapDataSourceApiTypesToStandard,
  POLICY_TYPES,
  SENSITIVE_LABEL
} from '../../constants'
import { IGetAttributesParams, IGetAttributesTableListParams } from '../attributes/attributesSlice'
import {
  getAfterCursor,
  parameterizeArrayofObjects,
  parameterizeFilterArray
} from '../../utils/graphqlUtil'
import { FilterParams, IDocument } from '../../interfaces'
import { DocumentClassCards } from '../../services/api/apiTypes'
import { GetAccessControlUserByObjectIdFilterParams } from '../accessControl/types'
import { mapStringToTimeStamp } from '../../utils/timeUtil'
import { camelCaseToUpperSnakeCase, isNumeric } from '../../utils/stringUtil'
import { gql } from 'graphql-request'

export const queryClassificationsGrouped = ({ filters }: ClassificationsGroupedParams): string => {
  const labelsInitFilter = filters?.filter?.find((filter) => filter.key === LABELS_KEY)
  // remove labels filter from filters array
  let documentLabelIds: string[] = []
  if (filters?.filter && labelsInitFilter) {
    documentLabelIds = labelsInitFilter.values
    filters.filter = filters.filter.filter((filter) => filter.key !== LABELS_KEY)
  }

  const filterString = filters && filters.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''
  const dateFilterString =
    filters && filters?.dateFilters && filters.dateFilters.length
      ? `dateFilters: ${parameterizeFilterArray(filters.dateFilters, ['key', 'start', 'end'])}`
      : ''

  const intRangeFilterString =
    filters && filters.intRangeFilters && filters.intRangeFilters.length
      ? `intRangeFilters: ${parameterizeFilterArray(filters.intRangeFilters, [
          'key',
          'min',
          'max'
        ])}`
      : ''

  let filter = ''
  if (filterString) {
    filter += `filter: ${filterString}`
  }
  if (booleanFilterString) {
    filter += `, booleanFilter: ${booleanFilterString} `
  }
  if (dateFilterString) {
    filter += `${dateFilterString}`
  }
  if (intRangeFilterString) {
    filter += `${intRangeFilterString}`
  }

  const documentLabelIdsFilter = documentLabelIds.length
    ? `, documentLabelIds: ${JSON.stringify(documentLabelIds)}`
    : ''

  const objectFilters =
    filter || documentLabelIdsFilter ? `(${filter}${documentLabelIdsFilter})` : ''

  return gql`
    {
      objectGroupByClassificationType ${objectFilters} {
        classification
        subclass
        objectCount
        riskyCount
      }
    }
  `
}

export const mapQueryClassificationsGrouped = (raw: any): ClassificationGroupedItem[] => {
  try {
    return raw.objectGroupByClassificationType.map((cl) => ({
      name: cl.classification,
      count: cl.objectCount || 0,
      subClass: cl.subclass,
      riskyCount: cl.riskyCount || 0
    }))
  } catch (e) {
    console.error(e)
    return []
  }
}

export const mapQueryAwsClassifiactionsList = (
  queryResponse: any,
  blobId?: string
): FileListResponseType => {
  try {
    if (blobId) {
      const list =
        queryResponse && queryResponse?.columns && queryResponse?.columns?.edges
          ? queryResponse?.columns?.edges[0]?.node?.blobCell?.edges?.map((item) => ({
              rowId: item?.node?.rowId,
              attributes: item?.node?.attributes
            }))
          : []
      return { list, total: queryResponse.columns.count || 0 }
    } else {
      const list =
        queryResponse && queryResponse.objects && queryResponse.objects.edges
          ? queryResponse.objects.edges.map(({ node }) => ({
              id: node.id,
              name: node.name,
              classification: node.classification,
              fileType: node.type,
              lastModifiedTime: node.lastModifiedTime,
              owner: node.owner,
              link: node.objectLink,
              metadataId: node.metadata?.incidentNumber || '',
              retainUntilTimestamp: node.retainUntilTimestamp,
              entitiesCount: node.entity.count,
              entities: node.entity?.edges.map(({ node: item }) => ({
                id: item.id,
                names: item.name
              })),
              attributesCount: node.attribute.count,
              attributes: node.attribute?.edges.map(({ node: item }) => ({
                internalName: item.internalName,
                name: item.name
              })),
              alertsCount: node.alert?.count,
              alerts: node.alert?.edges.map(({ node: item }) => ({
                id: item.id,
                name: item.name,
                severity: item.severity || ''
              })),
              buckets: node.bucket.edges.map(({ node: item }) => ({
                name: item.name
              })),
              drive: []
            }))
          : []

      return { list, total: queryResponse.objectCount.count || 0 }
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAccessUserByObjectId = (params: GetAccessControlUserByObjectIdFilterParams) => {
  const { id: objectId, pageSize, after } = params

  return gql`
    {
      objects(first: 1, id: "${objectId}") {
        edges {
          node {
            userAccess(first: ${pageSize}, after: "${after}") {
              totalCount: count
              edges {
                cursor
                node {
                  name
                  id
                  departmentName
                  employeeType
                  dataPrivilegeLevel
                  memberOfGroups {
                    edges {
                      node {
                        name
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryAccessUserByObjectId = (queryResponse: any): AccessUsersByObjectId => {
  const { userAccess } = queryResponse.objects.edges[0].node

  try {
    return {
      totalCount: userAccess.totalCount,
      users: userAccess.edges.map(({ node }) => ({
        name: node.name,
        id: node.id,
        departmentName: node.departmentName,
        employeeType: node.employeeType,
        dataPrivilegeLevel: node.dataPrivilegeLevel,
        memberOfGroups: node?.memberOfGroups?.edges?.map(({ node }) => ({
          id: node.id,
          name: node.name
        }))
      }))
    }
  } catch (error) {
    console.error(error)
    return { totalCount: 0, users: [] }
  }
}

export const queryAccessGroupByObjectId = (
  objectId: string,
  pageSize: number,
  after = '',
  datasourceId
) => gql`
{
  ${
    isNumeric(objectId)
      ? ` accessControlObjects(first: 1, objectIds: ${objectId},  datasourceIds: "${datasourceId}")`
      : `objects(first: 1, id: "${objectId}")`
  }
  {
      edges {
        node {
          groupAccess(first: ${pageSize}, after: "${after}") {
            totalCount: count
            edges {
              cursor
              node {
                name
                id
                memberUsers {
                  count
                }
              }
            }
          }
        }
      }
    }
  }
`

export const mapQueryAccessGroupByObjectId = (queryResponse: any): AccessGroupsByObjectId => {
  const objects = queryResponse?.objects || queryResponse?.accessControlObjects
  const { groupAccess } = objects.edges[0].node

  return {
    totalCount: groupAccess.totalCount,
    groups: groupAccess.edges.map(({ node }) => ({
      id: node.id,
      name: node.name,
      memberUsers: node.memberUsers
    }))
  }
}

export const queryObjectById = (id: string): string => gql`
  {
    objects(first: 1, id: "${id}") {
      edges {
        node {
     profileAttributeValues {
          profileAttributeValue {
            edges {
              node {
                key
                description
                profileAttribute {
                  edges {
                    node {
                      profileAttributeName
                    }
                  }
                }
              }
            }
          }
          description
        }
          workspace {
            edges {
              node {
                name
              }
            }
          }
          version
          sharedOn
          objectLink
          accessType
          lastModifiedTime
          retainUntilTimestamp
          owner
          messageId
          markedForRescan
          riskScore
          highSensitivityAttributes: attribute(sensitivity: ${SENSITIVE_LABEL.HIGH}) {
            count
          }
          mediumSensitivityAttributes: attribute(sensitivity: ${SENSITIVE_LABEL.MEDIUM}) {
            count
          }
          lowSensitivityAttributes: attribute(sensitivity: ${SENSITIVE_LABEL.LOW}) {
            count
          }
          userAccess {
            count
          }
          totalUserAccess: userAccess(first: 1, totalAccessorCount: true) {
            count
          }
          groupAccess {
            count
          }
          entity {
            count
          }
          datasource {
            edges {
              node {
                status: state
                idp {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          }
          alert(first: 2) {
            count
            edges {
              node {
                id
                severity
                policy {
                  edges {
                    node {
                      policyType {
                        edges {
                          node {
                            id
                            name
                          }
                        }
                      }
                    }
                  }
                }
                name
              }
            }
          }
          metadata {
            ... on ServiceNowMetadata {
              incidentNumber
            }
          }
          drive {
            edges {
              node {
                driveName
                site {
                  edges {
                    node {
                      displayName
                    }
                  }
                }
              }
            }
          }
          bucket(first: 1) {
            edges {
              node {
                name
                displayName
              }
            }
          }
        }
      }
    }
  }

`

export const mapQueryObjectById = (queryResponse: any): ObjectSummary => {
  try {
    const {
      sharedOn: sharedWhen = '',
      objectLink: link = '',
      lastModifiedTime = '',
      retainUntilTimestamp,
      owner,
      messageId = '',
      markedForRescan = false,
      highSensitivityAttributes: { count: highSensitivityAttributes } = { count: 0 },
      mediumSensitivityAttributes: { count: mediumSensitivityAttributes } = { count: 0 },
      lowSensitivityAttributes: { count: lowSensitivityAttributes } = { count: 0 },
      userAccess,
      totalUserAccess,
      groupAccess,
      accessType = '',
      metadata,
      drive,
      bucket,
      alert,
      entity,
      datasource,
      riskScore,
      profileAttributeValues,
      workspace,
      version
    } = queryResponse?.objects?.edges[0]?.node || {}
    return {
      lastModifiedTime,
      sharedWhen,
      link,
      accessType,
      incidentNumber: metadata?.incidentNumber || '',
      messageId,
      markedForRescan,
      driveName: drive?.edges[0]?.node?.driveName || '',
      siteName:
        drive?.edges[0]?.node?.site?.edges && drive.edges[0].node.site.edges.length > 0
          ? drive.edges[0].node.site.edges[0].node?.displayName
          : undefined,
      bucketName: bucket?.edges[0]?.node?.displayName || bucket?.edges[0]?.node?.name || '',
      dataSourceStatus: datasource?.edges[0]?.node?.status,
      idpId: datasource?.edges[0]?.node?.idp?.edges[0]?.node?.id,
      alerts: {
        list: alert?.edges?.map?.(({ node }) => {
          return node ? { name: node?.name, id: node?.id, severity: node?.severity } : {}
        }),
        count: alert?.count || 0,
        isViolatingRetentionPolicy: alert?.edges?.some?.(({ node }) => {
          const { policy } = node
          const policyType = policy?.edges?.[0]?.node?.policyType?.edges?.[0]?.node?.name
          return policyType === POLICY_TYPES.RETENTION
        }),
        isViolatingStaleFilesPolicy: alert?.edges?.some?.(({ node }) => {
          const { policy } = node
          const policyType = policy?.edges?.[0]?.node?.policyType?.edges?.[0]?.node?.name
          return policyType === POLICY_TYPES.STALE_FILES
        })
      },
      retainUntilTimestamp,
      owner,
      highSensitivityAttributes,
      mediumSensitivityAttributes,
      lowSensitivityAttributes,
      userAccess,
      totalUserAccess,
      groupAccess,
      entityCount: entity?.count,
      riskScore,
      profileAttributes: profileAttributeValues?.map((profileAttribute) => {
        return profileAttribute.profileAttributeValue?.edges?.map(({ node }) => {
          const { key, description } = node
          const profileAttributeName = node.profileAttribute.edges?.[0]?.node?.profileAttributeName
          return `${profileAttributeName}: ${key ? key + ' -' : ''} ${
            description || profileAttribute?.description
          }`
        })
      }),
      workspace: workspace?.edges[0]?.node?.name || '',
      version
    }
  } catch (e) {
    return { lastModifiedTime: '', sharedWhen: '', link: '' }
  }
}

export const queryAccessObjectById = (params: { id: string; datasourceId: string }): string => gql`
{
  accessControlObjects(objectIds: [${params.id}], datasourceIds: "${params.datasourceId}") {
    edges {
      node {
        id
        sensitivityLevel
        name
        driveName
        lastModifiedTime
        objectLink
        owner {
          edges {
            node {
              name
            }
          }
        }
        groupAccess(first: 10) {
          count
        }
        userAccess(first: 10) {
          count
        }
        totalUserAccess: userAccess(first: 1, totalAccessorCount: true) {
          count
        }
        fileShare {
          edges {
            node {
              name
            }
          }
        }
        datasource {
          edges {
            node {
              id
              name
              type
              status: state
              idp {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
`

export const mapQueryAccessObjectById = (queryResponse: any): AccessObjectSummary => {
  const object = queryResponse?.accessControlObjects?.edges?.[0]?.node
  return {
    id: object?.id,
    sensitivityLevel: object?.sensitivityLevel,
    name: object?.name,
    sharedWhen: '',
    driveName: object?.driveName,
    lastModifiedTime: object?.lastModifiedTime,
    link: object?.objectLink,
    owner: object?.owner?.edges?.[0]?.node?.name,
    shareName: object?.fileShare?.edges?.[0]?.node?.name,
    groupAccess: object?.groupAccess,
    userAccess: object?.userAccess,
    totalUserAccess: object?.totalUserAccess,
    dataSourceStatus: object?.datasource?.edges?.[0]?.node?.status,
    idpId: object?.datasource?.edges?.[0]?.node?.idp?.edges?.[0]?.node?.id,
    datasourceId: object?.datasource?.edges?.[0]?.node?.id,
    datasourceName: object?.datasource?.edges?.[0]?.node?.name,
    datasourceType: object?.datasource?.edges?.[0]?.node?.type
  }
}

export const queryTopClassification = (params: IGetAttributesParams): string => {
  const { userEntityId, datasourceId } = params
  let paramStr = ''
  if (userEntityId) {
    paramStr += `entityId:"${userEntityId}"`
  }
  if (datasourceId) {
    paramStr += `datasourceIds: ["${datasourceId}"]`
  }
  if (paramStr) {
    paramStr = `(${paramStr})`
  }
  return gql`
  {
    attributeInstanceGroupedbyDocumentClass${paramStr} {
      documentClass
      attributeInstanceCount
    }
  }`
}

export const queryObjectsByDataSource = (params?: ObjectsByDataSourceParams): string => {
  const { filters } = params || {}

  let filter = ''
  if (filters && !!Object.values(filters).length) {
    const filtersStr = Object.entries(filters).map(([key, values]) => {
      return `{key: ${key}, values: ${JSON.stringify(values)}}`
    })
    filter += `filter: [${filtersStr}],`
  }

  return gql`
    {
      datasources {
        count
        edges {
          node {
            id
            name
            type
            objects: objects(first: 1, ${filter}) {
              count
            }
            structuredObjects: tables(first: 1) {
              count
            }
            riskyObjects: objects(first: 1, ${filter}, booleanFilter: [{key: IS_RISKY, value: true}]) {
              count
            }
            sensitiveObjects: objects(first: 1, isSensitive: true, ${filter}) {
              count
            }
          }
        }
      }
    }
  `
}

export const mapQueryObjectsByDataSource = (queryResponse: any): IDocument[] => {
  try {
    const list =
      queryResponse && queryResponse.datasources && queryResponse.datasources.edges
        ? queryResponse.datasources.edges.map(({ node }) => ({
            datasourceId: node.id,
            datasourceName: node.name,
            datasourceType: node.type,
            objectsCount: node.objects.count || 0,
            structuredObjectsCount: node.structuredObjects.count || 0,
            riskyObjectsCount: node.riskyObjects.count || 0,
            sensitiveObjectsCount: node.sensitiveObjects.count || 0
          }))
        : []
    return list
  } catch (e) {
    return []
  }
}

export const queryObjectsCounts = (params: any) => {
  const { datasourceId } = params

  const datasourceFilter = datasourceId
    ? `filter: [{ key: DATASOURCE_IDS, values: ["${datasourceId}"] }]`
    : ''

  const datasourceArg = datasourceId ? `(datasourceIds: ["${datasourceId}"])` : ''

  return gql`
    {
      totalObjectsCount: objects(
        ${datasourceFilter}
        category: FILE
      ) {
        count
      }

      sensitiveObjectsCount: objects(
        ${datasourceFilter}
        isSensitive: true
        category: FILE
      ) {
        count
      }

      datasources${datasourceArg} {
        edges {
          node {
            totalDuplicateBytes
            numDuplicateObjects
          }
        }
      }
    }
  `
}

export const mapQueryObjectsCount = (raw: any) => {
  try {
    const datasourceEdges = raw.datasources?.edges || []

    const duplicateData = datasourceEdges.reduce(
      (acc: any, edge: any) => {
        const node = edge?.node
        return {
          totalDuplicateBytes: acc.totalDuplicateBytes + (node?.totalDuplicateBytes || 0),
          numDuplicateObjects: acc.numDuplicateObjects + (node?.numDuplicateObjects || 0)
        }
      },
      { totalDuplicateBytes: 0, numDuplicateObjects: 0 }
    )

    return {
      totalObjectsCount: raw.totalObjectsCount.count,
      sensitiveObjectsCount: raw.sensitiveObjectsCount.count,
      duplicateObjectsCount: duplicateData.numDuplicateObjects,
      duplicateBytesCount: duplicateData.totalDuplicateBytes
    }
  } catch (e) {
    console.error(e)
    return {
      totalObjectsCount: 0,
      sensitiveObjectsCount: 0,
      duplicateObjectsCount: 0,
      duplicateBytesCount: 0
    }
  }
}

const getFilterWithEnum = (
  filters: {
    key: string
    values: string[]
  }[]
) => {
  return filters.map(({ key, values }) => {
    return { key: camelCaseToUpperSnakeCase(key), values }
  })
}

export const queryDocumentList = (
  params: IGetAttributesParams &
    IGetAttributesTableListParams & {
      filters?: FilterParams
      retainUntil?: string
      lastModifiedTime?: string
      retainUntilTime?: string
      deleteAfterTimestamp?: string
      pageSize?: number
      withEntityDetails?: boolean
      sortOrder?: number
    }
): string => {
  const {
    datasourceId,
    datasourceType,
    page,
    filters: _filters,
    pageSize = LIMIT_DEFAULT,
    withEntityDetails
  } = params

  const filters = JSON.parse(JSON.stringify(_filters))
  const isWorkday = datasourceType === DATA_SOURCE_TYPES.workday
  const isIManage = datasourceType === DATA_SOURCE_TYPES.iManage

  // Labels - done this way to support reports
  let labelFilter
  const labelFilterIndex = filters?.filter?.findIndex(({ key }) => key === LABELS_KEY)
  if (labelFilterIndex && labelFilterIndex >= 0) {
    labelFilter = filters?.filter?.[labelFilterIndex]
    filters?.filter?.splice(labelFilterIndex, 1)
  }

  const cursor = getAfterCursor(page, pageSize)

  const dynamicFiltersWithEnum = getFilterWithEnum(filters?.dynamicFilters ?? [])

  let filterString = ''

  if (isWorkday) {
    const findOwnerFilter = filters?.filter?.findIndex((filter) => filter.key === 'OWNER')

    if (findOwnerFilter !== undefined && findOwnerFilter > -1) {
      const ownerValues = filters?.filter?.[findOwnerFilter]?.values
      filters?.filter?.splice(findOwnerFilter, 1)
      filters?.filter?.push({
        key: 'USER_ID',
        values: ownerValues
      })
    }
  }

  filterString =
    filters && filters.filter
      ? parameterizeArrayofObjects([...filters.filter, ...(dynamicFiltersWithEnum ?? [])])
      : ''

  let paramStr = ''

  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''

  const dateFilterString =
    filters && filters?.dateFilters && filters.dateFilters.length
      ? `dateFilters: ${parameterizeFilterArray(filters.dateFilters, ['key', 'start', 'end'])}`
      : ''

  const intRangeFilterString =
    filters && filters.intRangeFilters && filters.intRangeFilters.length
      ? `intRangeFilters: ${parameterizeFilterArray(filters.intRangeFilters, [
          'key',
          'min',
          'max'
        ])}`
      : ''

  if (datasourceId) {
    paramStr += `, datasourceIds:"${datasourceId}"`
  }
  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (labelFilter) {
    paramStr += `, documentLabelIds: ${JSON.stringify(labelFilter.values)}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString} `
  }
  if (dateFilterString) {
    paramStr += `, ${dateFilterString}`
  }
  if (intRangeFilterString) {
    paramStr += `, ${intRangeFilterString}`
  }

  if (paramStr) {
    paramStr = `(first: ${pageSize} ${paramStr},after:"${cursor}", category:FILE)`
  }

  const isDrives =
    datasourceType === DATA_SOURCE_TYPES.gdrive ||
    datasourceType === DATA_SOURCE_TYPES.gdrivePersonal ||
    datasourceType === DATA_SOURCE_TYPES.oneDrive ||
    datasourceType === DATA_SOURCE_TYPES.box ||
    datasourceType === DATA_SOURCE_TYPES.dropbox ||
    datasourceType === DATA_SOURCE_TYPES.netDocuments ||
    datasourceType === DATA_SOURCE_TYPES.iManage
  const FRAGMENT_DRIVES = `
    drive {
      edges {
        node { id, driveName }
      }
    }
  `

  const isBuckets =
    datasourceType === DATA_SOURCE_TYPES.aws ||
    datasourceType === DATA_SOURCE_TYPES.azureBlob ||
    datasourceType === DATA_SOURCE_TYPES.googleCloudStorage
  const FRAGMENT_BUCKETS = `
    bucket{
      edges{
        node{
          name
          displayName
        }
      }
    }
  `

  const isTickets = datasourceType === DATA_SOURCE_TYPES.jira
  const FRAGMENT_TICKETS = `
    ticket {
      edges {
        node {
          id
          lastModifiedTime
          ticketName
          ticketLink
          ticketStoreProject {
            edges {
              node {
                projectName
              }
            }
          }
          reporter {
            name
          }
          assignee {
            name
          }
        }
      }
    }
  `

  const isMails = MAIL_DATASOURCES.includes(datasourceType as DATA_SOURCE_TYPES)
  const FRAGMENT_MAILS = `
    mail {
      edges {
        node {
          id
          isDeleted
          messageId
        }
      }
    }
  `

  const isIncidents = datasourceType === DATA_SOURCE_TYPES.serviceNow
  const FRAGMENT_INCIDENTS = `
    metadata {
      ... on ServiceNowMetadata {
        incidentNumber
      }
    }
  `

  const FRAGMENT_ENTITY_DETAILS = `
    edges {
      node {
        id
        name
      }
    }
  `

  return gql`
  {
    objects${paramStr}{
      edges {
        node {
          id,
         profileAttributeValues {
            profileAttributeValue {
              edges {
                node {
                  key
                  description
                  profileAttribute {
                    edges {
                      node {
                        profileAttributeName
                      }
                    }
                  }
                }
              }
          }
          description
        }
        workspace {
            edges {
              node {
                name
              }
            }
          }
          name,
          classification,
          size,
          version,
          type,
          markedForRescan,
          lastModifiedTime,
          createdAt,
          owner,
          objectLink,
          stream,
          skipReason,
          ${isWorkday ? `positionTitle, employmentType, status` : ''}
          retainUntilTimestamp,
          riskScore,
          alert(first:5, filter: [{key: STATUS, values: ["Active"]}]){
            count
            edges{
              node{
                id
                name
                severity
              }
            }
          }
          labels {
            edges {
              node {
                id
                name
                labelSetId
                labelSetName
                mappedLabel {
                  edges {
                    node {
                      id
                      name
                      labelSetId
                      labelSetName
                    }
                  }
                }
              }
            }
          }
          attribute {
            count
            edges {
              node {
                internalName
                name
              }
            }
          }
          entity(first: ${ENTITY_DETAILS_LIMIT}) {
            count
            ${withEntityDetails ? FRAGMENT_ENTITY_DETAILS : ''}
          }
          ${isDrives ? FRAGMENT_DRIVES : ''}
          ${isBuckets ? FRAGMENT_BUCKETS : ''}
          ${isTickets ? FRAGMENT_TICKETS : ''}
          ${isMails ? FRAGMENT_MAILS : ''}
          ${isIncidents ? FRAGMENT_INCIDENTS : ''}
          ${isIManage ? 'workspaceName' : ''}
        }
      }
    }
  }`
}

export const mapQueryDocumentList = (queryResponse: any): FileListResponseType => {
  try {
    const list =
      queryResponse?.objects?.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        classification: node.classification,
        profileAttributes: node?.profileAttributeValues?.map((profileAttribute) => {
          return profileAttribute.profileAttributeValue?.edges?.map(({ node }) => {
            const { key, description } = node
            const profileAttributeName =
              node.profileAttribute.edges?.[0]?.node?.profileAttributeName
            return `${profileAttributeName}: ${key ? key + ' -' : ''} ${
              description || profileAttribute?.description
            }`
          })
        }),
        workspace: node?.workspace?.edges[0]?.node?.name || node?.workspaceName || '',
        version: node.version,

        skipReason: node.skipReason || '',
        fileSize: node.size,
        fileType: node.type,
        markedForRescan: node.markedForRescan,
        lastModifiedTime: node.lastModifiedTime,
        createdAt: node.createdAt,
        retainUntilTimestamp: node.retainUntilTimestamp,
        owner: node.owner,
        riskScore: node?.riskScore,
        isDeleted: node?.mail?.edges?.some(({ node }) => node.isDeleted) || false,
        messageId: node?.mail?.edges[0]?.node?.messageId || '',
        link: node.objectLink,
        stream: node.stream,
        metadataId: node.metadata?.incidentNumber || '',
        positionTitle: node?.positionTitle,
        employmentType: node?.employmentType,
        status: node?.status,
        drive: node?.drive?.edges
          ? {
              id: node?.drive?.edges[0]?.node?.id,
              name: node?.drive?.edges[0]?.node?.driveName
            }
          : { id: '', name: '' },
        entitiesCount: node.entity.count,
        entities: node?.entity?.edges?.map(({ node }) => ({
          id: node.id,
          names: node.name
        })),
        attributesCount: node.attribute.count,
        attributes: node?.attribute?.edges.map(({ node }) => ({
          internalName: node.internalName,
          name: node.name
        })),
        alertsCount: node.alert?.count,
        alerts: node?.alert?.edges.map(({ node }) => ({
          id: node.id,
          name: node.name,
          severity: node.severity || ''
        })),
        projectName:
          node?.ticket?.edges[0]?.node?.ticketStoreProject?.edges[0]?.node?.projectName || '',
        ticketName: node?.ticket?.edges[0]?.node?.ticketName || '',
        ticketId: node?.ticket?.edges[0]?.node?.id || '',
        ticketLink: node?.ticket?.edges[0]?.node?.ticketLink || '',
        reporter: node?.ticket?.edges[0]?.node?.reporter?.name || '',
        assignee: node?.ticket?.edges[0]?.node?.assignee?.name || '',
        ticketLastModifiedTime: node?.ticket?.edges[0]?.node?.lastModifiedTime || '',
        buckets: node?.bucket?.edges?.map(({ node: item }) => ({
          name: item.displayName || item.name
        })),
        labels: node?.labels?.edges.map(({ node: label }) => ({
          id: label.id,
          name: label.name,
          labelSetId: label.labelSetId,
          labelSetName: label.labelSetName,
          mappedLabelId: label.mappedLabel.edges?.[0]?.node?.id || '',
          mappedLabelName: label.mappedLabel.edges?.[0]?.node?.name || '',
          mappedLabelSetId: label.mappedLabel.edges?.[0]?.node?.labelSetId || '',
          mappedLabelSetName: label.mappedLabel.edges?.[0]?.node?.labelSetName || ''
        }))
      })) || []

    return { list }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    return { list: [] }
  }
}

export const queryDocumentCount = (
  params: IGetAttributesParams &
    IGetAttributesTableListParams & {
      filters?: FilterParams
      retainUntil?: string
      lastModifiedTime?: string
      retainUntilTime?: string
      deleteAfterTimestamp?: string
    }
): string => {
  const { datasourceId, page, filters: _filters } = params

  const filters = JSON.parse(JSON.stringify(_filters))

  // Labels
  let labelFilter
  const labelFilterIndex = filters?.filter?.findIndex(({ key }) => key === LABELS_KEY)
  if (labelFilterIndex && labelFilterIndex >= 0) {
    labelFilter = filters?.filter?.[labelFilterIndex]
    filters?.filter?.splice(labelFilterIndex, 1)
  }

  const cursor = getAfterCursor(page, LIMIT_DEFAULT)

  let paramStr = ''
  const filterString = filters && filters.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''
  const dateFilterString =
    filters && filters?.dateFilters && filters.dateFilters.length
      ? `dateFilters: ${parameterizeFilterArray(filters.dateFilters, ['key', 'start', 'end'])}`
      : ''

  const intRangeFilterString =
    filters && filters.intRangeFilters && filters.intRangeFilters.length
      ? `intRangeFilters: ${parameterizeFilterArray(filters.intRangeFilters, [
          'key',
          'min',
          'max'
        ])}`
      : ''

  if (datasourceId) {
    paramStr += `, datasourceIds:"${datasourceId}"`
  }
  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (labelFilter) {
    paramStr += `, documentLabelIds: ${JSON.stringify(labelFilter.values)}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString} `
  }
  if (dateFilterString) {
    paramStr += `${dateFilterString}`
  }
  if (intRangeFilterString) {
    paramStr += `${intRangeFilterString}`
  }
  if (paramStr) {
    paramStr = `(first: ${LIMIT_DEFAULT},${paramStr},after:"${cursor}", category:FILE)`
  }

  return gql`
  {
    objectsCount: objects${paramStr}{
      count
    }
  }`
}

export const mapQueryDocumentCount = (queryResponse: any): FileListResponseType => {
  try {
    return { total: queryResponse.objectsCount.count || 0 }
  } catch (e) {
    return { total: 0 }
  }
}

export const queryDocumentListByBlob = (
  params: IGetAttributesParams &
    IGetAttributesTableListParams & {
      filters?: FilterParams
      retainUntil?: string
      lastModifiedTime?: string
      retainUntilTime?: string
      deleteAfterTimestamp?: string
    }
): string => {
  const { blobId } = params
  return gql`
  {
      columns(id: "${blobId}") {
        edges {
          node {
            name
            blobCell(first: 50) {
              edges {
                node {
                  rowId
                  attributes
                }
              }
            }
          }
        }
      }
  }`
}

export const mapQueryDocumentListByBlob = (queryResponse: any): FileListResponseType => {
  try {
    const list: ClassificationObject[] =
      queryResponse?.columns?.edges[0]?.node?.blobCell?.edges?.map((item) => ({
        rowId: item?.node?.rowId,
        attributes: item?.node?.attributes
      })) || []
    return { list, total: queryResponse.columns.count || 0 }
  } catch (e) {
    return { list: [], total: 0 }
  }
}

// TODO: add type for graphql response
export const mapQueryTopClassification = (raw: any): ClassificationTopWidget[] => {
  return raw.attributeInstanceGroupedbyDocumentClass.map((docClass) => ({
    documentClass: docClass.documentClass,
    attributeInstanceCount: docClass.attributeInstanceCount || 0
  }))
}

export const queryClassificationsList = (
  params: ClassifiedObjectsParams & {
    lastModifiedTime?: string
    loadMipTags?: boolean
  }
): string => {
  const { datasourceId, filters, page, lastModifiedTime, loadMipTags } = params

  // remove labels filter from filters array
  const labelsInitFilter = params.filters?.filter?.find((filter) => filter.key === LABELS_KEY)
  let documentLabelIds: string[] = []
  if (params.filters?.filter && labelsInitFilter) {
    documentLabelIds = labelsInitFilter.values
    params.filters.filter = params.filters.filter.filter((filter) => filter.key !== LABELS_KEY)
  }
  const documentLabelIdsFilter = documentLabelIds.length
    ? `, documentLabelIds: ${JSON.stringify(documentLabelIds)}`
    : ''

  const { startTimestamp, endTimestamp } = mapStringToTimeStamp(lastModifiedTime)
  const dateFilter = lastModifiedTime
    ? `dateFilter: {key: ${FILTER_LAST_MODIFIED_TIME_KEY}, start: ${startTimestamp}, end:${endTimestamp} }`
    : ''

  const intRangeFilterString =
    filters && filters?.intRangeFilter?.key
      ? `intRangeFilter: {key: ${filters.intRangeFilter.key}, min: ${filters.intRangeFilter.min}, max: ${filters.intRangeFilter.max} }`
      : ''
  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  let paramStr = `${dateFilter}`
  const filterString = filters && filters.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''
  if (datasourceId) {
    paramStr += `, datasourceIds:"${datasourceId}"`
  }
  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString} `
  }
  if (intRangeFilterString) {
    paramStr += `${intRangeFilterString}`
  }
  const queryAccessType = `${loadMipTags ? 'accessType,' : ''}`

  return gql`
    {
      objects(first: ${LIMIT_DEFAULT} ${documentLabelIdsFilter} ,${paramStr},after:"${cursor}", category:FILE) {
        edges{
        node{
          id
          name,
          classification,
          subclass,
          ${queryAccessType}
          type,
          lastModifiedTime,
          owner,
          objectLink,
          retainUntilTimestamp
          riskScore
          metadata {
            ... on ServiceNowMetadata {
              incidentNumber
            }
          }
          alert(first:5, filter: [{key: STATUS, values: ["Active"]}]){
            count
            edges {
              node {
                id
                name
                severity
              }
            }
          }
          attribute {
            edges {
              node{
                internalName
                name
              }
            }
            count
          }
          entity {
            edges {
              node {
                id
                name
              }
            }
            count
          }
          datasource {
            edges {
              node {
                id
                name
                type
              }
            }
          }
          labels {
            edges {
              node {
                id
                name
                labelSetId
                labelSetName
                mappedLabel {
                  edges {
                    node {
                      id
                      name
                      labelSetId
                      labelSetName
                    }
                  }
                }
              }
            }
          }
        }
      }
      }
    }`
}
export const mapQueryClassificationsList = (queryResponse: any): FileListResponseType => {
  try {
    const list = queryResponse.objects.edges.map(({ node }) => ({
      id: node.id,
      name: node.name,
      classification: node.classification,
      ...(node.accessType ? { accessType: node.accessType } : {}),
      subclass: node.subclass,
      fileType: node.type,
      lastModifiedTime: node.lastModifiedTime,
      retainUntilTimestamp: node.retainUntilTimestamp,
      owner: node.owner,
      riskScore: node?.riskScore,
      isDeleted: node?.mail?.edges?.some(({ node }) => node.isDeleted) || false,
      link: node.objectLink,
      metadataId: node.metadata?.incidentNumber || '',
      entitiesCount: node.entity.count,
      entities: node?.entity?.edges.map(({ node }) => ({
        id: node.id,
        names: node.name
      })),
      attributesCount: node.attribute.count,
      attributes: node?.attribute?.edges.map(({ node }) => ({
        internalName: node.internalName,
        name: node.name
      })),
      alertsCount: node.alert?.count,
      alerts: node?.alert?.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        severity: node.severity || ''
      })),
      datasourceId: node?.datasource?.edges[0]?.node?.id || '',
      datasourceName: node?.datasource?.edges[0]?.node?.name || '',
      datasourceType: node?.datasource?.edges[0]?.node?.type || '',
      labels: node?.labels?.edges.map(({ node: label }) => ({
        id: label.id,
        name: label.name,
        labelSetId: label.labelSetId,
        labelSetName: label.labelSetName,
        mappedLabelId: label.mappedLabel.edges?.[0]?.node?.id || '',
        mappedLabelName: label.mappedLabel.edges?.[0]?.node?.name || '',
        mappedLabelSetId: label.mappedLabel.edges?.[0]?.node?.labelSetId || '',
        mappedLabelSetName: label.mappedLabel.edges?.[0]?.node?.labelSetName || ''
      }))
    }))

    return { list }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryClassificationsListCount = (
  params: ClassifiedObjectsParams & {
    lastModifiedTime?: string
    loadMipTags?: boolean
  }
): string => {
  const { datasourceId, filters, page, lastModifiedTime } = params

  // remove labels filter from filters array
  const labelsInitFilter = params.filters?.filter?.find((filter) => filter.key === LABELS_KEY)
  let documentLabelIds: string[] = []
  if (params.filters?.filter && labelsInitFilter) {
    documentLabelIds = labelsInitFilter.values
    params.filters.filter = params.filters.filter.filter((filter) => filter.key !== LABELS_KEY)
  }
  const documentLabelIdsFilter = documentLabelIds.length
    ? `, documentLabelIds: ${JSON.stringify(documentLabelIds)}`
    : ''

  const { startTimestamp, endTimestamp } = mapStringToTimeStamp(lastModifiedTime)
  const dateFilter = lastModifiedTime
    ? `dateFilter: {key: ${FILTER_LAST_MODIFIED_TIME_KEY}, start: ${startTimestamp}, end:${endTimestamp} }`
    : ''

  const intRangeFilterString =
    filters && filters?.intRangeFilter?.key
      ? `intRangeFilter: {key: ${filters.intRangeFilter.key}, min: ${filters.intRangeFilter.min}, max: ${filters.intRangeFilter.max} }`
      : ''
  const cursor = getAfterCursor(page, LIMIT_DEFAULT)
  let paramStr = `${dateFilter}`
  const filterString = filters && filters.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''
  if (datasourceId) {
    paramStr += `, datasourceIds:"${datasourceId}"`
  }
  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString} `
  }
  if (intRangeFilterString) {
    paramStr += `${intRangeFilterString}`
  }

  return gql`
    {
      objectsCount: objects(first: ${LIMIT_DEFAULT} ${documentLabelIdsFilter} ,${paramStr},after:"${cursor}", category:FILE){
        count
      }
    }`
}
export const mapQueryClassificationsListCount = (queryResponse: any): { total: number } => {
  try {
    return { total: queryResponse.objectsCount.count || 0 }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryClassificationsPrint = (): string => gql`
  {
    objectGroupByClassificationType {
      classification
      objectCount
      subclass
    }
  }
`

export const mapQueryClassificationsPrint = (raw: any): ClassificationsPrint[] => {
  try {
    const list = raw.objectGroupByClassificationType.map((item) => ({
      classification: item.classification,
      objectsCount: item.objectCount,
      subclasses: item.subclass
    }))

    return list
  } catch (e) {
    return []
  }
}

export const parseAnalyticsFile = (raw: any): AnalyticsItem[] => {
  try {
    const parsedData = raw.map((ds) => {
      return {
        id: ds.datasourceId || '',
        name: ds.datasourceName || '',
        type: mapDataSourceApiTypesToStandard(ds.datasourceType) || 'GENERIC',
        size: ds.totalSize || 0,
        sharedDriveSize: ds.totalSharedDriveSize || 0,
        personalDriveSize: ds.totalPersonalDriveSize || 0,
        duplicatesCount: ds.totalDuplicateFiles || 0,
        duplicatesSize: ds.totalDuplicateFileSize || 0,
        lastScan: ds.lastScanTimeStamp || 0,
        distrByFile: ds.distributionByFileType || [{ key: 'OTHERS', value: 1 }],
        distrBySize: ds.distributionBySize || [{ key: 'FROM_16_TO_128KB', value: 1 }],
        distrByUpdate: ds.distributionByUpdateTime || [{ key: 'FROM_0_TO_3MONTHS', value: 1 }]
      }
    })
    return parsedData
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryFilesGroupedByLabelSet = (datasourceId?: string): string => {
  const dsFilter = datasourceId ? `datasourceIds:"${datasourceId}"` : ''
  const filters = dsFilter ? `(${dsFilter})` : ''
  return gql`
    {
      documentLabelSet${filters} {
        edges {
          node {
            id
            name
            labels {
              count
              edges {
                node {
                  id
                  name
                  type
                  objectCount
                  piiObjectsCount
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryFilesGroupedByLabelSet = (raw: any): DocumentClassCards[] => {
  try {
    const matrix = raw.documentLabelSet?.edges?.map(({ node: set }) => {
      return set.labels.edges.map(({ node: label }) => ({
        labelSetId: set.id,
        labelSetName: set.name,
        accessType: set.name,
        documentsCount: label?.objectCount,
        riskyDocumentsCount: label?.piiObjectsCount,
        labelId: label.id,
        labelName: label.name,
        type: label.type
      }))
    })
    return matrix.flat()
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryRescanDocuments = ({ datasourceId, documentIds }: any): string => {
  return gql`
    mutation {
      rescanDocuments(
        actionInput: {
          clientMutationId: "1"
          datasourceId: "${datasourceId}"
          documentIds: ${JSON.stringify(documentIds)}
        }
      ) {
        clientMutationId
        status
      }
    }
  `
}

export interface ObjectCountGroupByLabelParams {
  datasourceId: string
  entityId: string
}

export interface ObjectCountGroupByLabelItem {
  labelId: string
  objectCount: number
  sensitiveObjectCount: number
}

export const queryObjectCountGroupByLabel = ({
  datasourceId,
  entityId
}: ObjectCountGroupByLabelParams): string => {
  return gql`
    {
      objectCountGroupByLabel(
        datasourceIds: "${datasourceId}"
        entityIds: "${entityId}"
      ) {
        edges {
          node {
            labelId
            objectCount
            sensitiveObjectCount
          }
        }
      }
    }
  `
}

export const mapQueryObjectCountGroupByLabel = (raw: any): ObjectCountGroupByLabelItem[] => {
  try {
    return (
      raw.objectCountGroupByLabel?.edges?.map(({ node }) => ({
        labelId: node.labelId,
        objectCount: node.objectCount || 0,
        sensitiveObjectCount: node.sensitiveObjectCount || 0
      })) || []
    )
  } catch (error) {
    console.error(error)
    return []
  }
}

export const rescanEmailsMutation = (args: { dataSourceId: string; mailIds: string[] }) => {
  return gql`
    mutation {
      rescanDocuments(
        actionInput: {
          clientMutationId: "1"
          datasourceId: "${args.dataSourceId}"
          mailIds: ${JSON.stringify(args.mailIds)}
        }
      ) {
        clientMutationId
        status
      }
    }
  `
}

export const queryDuplicateFilesList = (
  params: IGetAttributesParams &
    IGetAttributesTableListParams & {
      filters?: FilterParams
      datasourceId?: string
      vectorDbId?: string
      sortField?: string
      sortOrder?: number
    }
): string => {
  const {
    datasourceId,
    page,
    filters: _filters,
    pageSize = LIMIT_DEFAULT,
    vectorDbId,
    sortField,
    sortOrder
  } = params

  const filters = JSON.parse(JSON.stringify(_filters)) || {
    filter: [],
    booleanFilter: [],
    dateFilters: [],
    intRangeFilters: [],
    dynamicFilters: []
  }
  const cursor = getAfterCursor(page, pageSize)

  if (datasourceId) {
    const datasourceFilter = filters.filter?.find((f) => f.key === 'DATASOURCE_IDS')
    if (!datasourceFilter) {
      if (!filters.filter) {
        filters.filter = []
      }
      filters.filter.push({ key: 'DATASOURCE_IDS', values: [datasourceId] })
    }
  }

  if (vectorDbId) {
    const vectorDbFilter = filters.filter?.find((f) => f.key === 'VECTOR_DB_ID')
    if (!vectorDbFilter) {
      if (!filters.filter) {
        filters.filter = []
      }
      filters.filter.push({ key: 'VECTOR_DB_ID', values: [vectorDbId] })
    }
  }

  const dynamicFiltersWithEnum = getFilterWithEnum(filters?.dynamicFilters ?? [])

  let filterString = ''
  filterString =
    filters && filters.filter
      ? parameterizeArrayofObjects([...filters.filter, ...(dynamicFiltersWithEnum ?? [])])
      : ''

  let paramStr = ''

  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''

  const dateFilterString =
    filters && filters?.dateFilters && filters.dateFilters.length
      ? `dateFilters: ${parameterizeFilterArray(filters.dateFilters, ['key', 'start', 'end'])}`
      : ''

  const intRangeFilterString =
    filters && filters.intRangeFilters && filters.intRangeFilters.length
      ? `intRangeFilters: ${parameterizeFilterArray(filters.intRangeFilters, [
          'key',
          'min',
          'max'
        ])}`
      : ''

  paramStr = `first: ${pageSize}, after:"${cursor}"`

  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString}`
  }
  if (dateFilterString) {
    paramStr += `, ${dateFilterString}`
  }
  if (intRangeFilterString) {
    paramStr += `, ${intRangeFilterString}`
  }
  const duplicateFilesSortFieldMap: Record<string, string> = {
    createdAt: 'CREATED_AT',
    lastModifiedTime: 'LAST_MODIFIED_TIME',
    size: 'SIZE'
  }
  const resolvedSortField = sortField ? duplicateFilesSortFieldMap[sortField] || sortField : null
  if (resolvedSortField) {
    paramStr += `, sortField: ${resolvedSortField}`
    if (sortOrder !== undefined) {
      paramStr += `, sortByAsc: ${sortOrder === 1 ? 'true' : 'false'}`
    }
  }

  paramStr = `(${paramStr})`

  return gql`
    {
      objectGroupByVectorDbId${paramStr} {
        count
        edges {
          node {
            id
            groupName
            name
            fileType
            size
            skipped
            skipReason
            checksum
            datasourceName
            lastModifiedTime
            createdAt
            siteId
            driveId
            fileId
            objectLink
            attributeInstanceCount
            classification
            version
            type
            markedForRescan
            owner
            retainUntilTimestamp
            riskScore
            profileAttributeValues {
              profileAttributeValue {
                edges {
                  node {
                    key
                    description
                    profileAttribute {
                      edges {
                        node {
                          profileAttributeName
                        }
                      }
                    }
                  }
                }
              }
              description
            }
            workspace {
              edges {
                node {
                  name
                }
              }
            }
            alert(first: 5, filter: [{key: STATUS, values: ["Active"]}]) {
              count
              edges {
                node {
                  id
                  name
                  severity
                }
              }
            }
            labels {
              edges {
                node {
                  id
                  name
                  labelSetId
                  labelSetName
                  mappedLabel {
                    edges {
                      node {
                        id
                        name
                        labelSetId
                        labelSetName
                      }
                    }
                  }
                }
              }
            }
            attribute {
              count
              edges {
                node {
                  internalName
                  name
                }
              }
            }
            entity(first: 10) {
              count
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  `
}
export const mapQueryDuplicateFilesList = (queryResponse: any): FileListResponseType => {
  try {
    const list =
      queryResponse?.objectGroupByVectorDbId?.edges?.map(({ node }) => ({
        id: node.id || node.fileId || node.groupName,
        name: node.name,
        groupName: node.groupName,
        fileType: node.fileType,
        size: node.size,
        skipped: node.skipped,
        skipReason: node.skipReason,
        checksum: node.checksum,
        datasourceName: node.datasourceName,
        lastModifiedTime: node.lastModifiedTime,
        createdAt: node.createdAt,
        siteId: node.siteId,
        driveId: node.driveId,
        fileId: node.fileId,
        link: node.objectLink,
        attributesCount: node.attributeInstanceCount || 0,
        classification: node.classification,
        version: node.version,
        type: node.type,
        markedForRescan: node.markedForRescan,
        owner: node.owner,
        retainUntilTimestamp: node.retainUntilTimestamp,
        riskScore: node.riskScore,
        profileAttributeValues: node.profileAttributeValues,
        workspace: node.workspace?.edges?.[0]?.node?.name,
        alerts:
          node.alert?.edges?.map((alertEdge) => ({
            id: alertEdge.node.id,
            name: alertEdge.node.name,
            severity: alertEdge.node.severity
          })) || [],
        alertCount: node.alert?.count || 0,
        labels:
          node.labels?.edges?.map((labelEdge) => ({
            id: labelEdge.node.id,
            name: labelEdge.node.name,
            labelSetId: labelEdge.node.labelSetId,
            labelSetName: labelEdge.node.labelSetName,
            mappedLabels:
              labelEdge.node.mappedLabel?.edges?.map((mappedEdge) => ({
                id: mappedEdge.node.id,
                name: mappedEdge.node.name,
                labelSetId: mappedEdge.node.labelSetId,
                labelSetName: mappedEdge.node.labelSetName
              })) || []
          })) || [],
        attributes:
          node.attribute?.edges?.map((attrEdge) => ({
            internalName: attrEdge.node.internalName,
            name: attrEdge.node.name
          })) || [],
        attributeDetailsCount: node.attribute?.count || 0,
        entities:
          node.entity?.edges?.map((entityEdge) => ({
            id: entityEdge.node.id,
            name: entityEdge.node.name
          })) || [],
        entityCount: node.entity?.count || 0
      })) || []

    const total = queryResponse?.objectGroupByVectorDbId?.count || 0

    return { list, total }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryDuplicateFilesCount = (
  params: IGetAttributesParams &
    IGetAttributesTableListParams & {
      filters?: FilterParams
      datasourceId?: string
      vectorDbId?: string
    }
): string => {
  const { datasourceId, filters: _filters, vectorDbId } = params

  const filters = JSON.parse(JSON.stringify(_filters)) || {
    filter: [],
    booleanFilter: [],
    dateFilters: [],
    intRangeFilters: [],
    dynamicFilters: []
  }

  if (datasourceId) {
    const datasourceFilter = filters.filter?.find((f) => f.key === 'DATASOURCE_IDS')
    if (!datasourceFilter) {
      if (!filters.filter) {
        filters.filter = []
      }
      filters.filter.push({ key: 'DATASOURCE_IDS', values: [datasourceId] })
    }
  }

  if (vectorDbId) {
    const vectorDbFilter = filters.filter?.find((f) => f.key === 'VECTOR_DB_ID')
    if (!vectorDbFilter) {
      if (!filters.filter) {
        filters.filter = []
      }
      filters.filter.push({ key: 'VECTOR_DB_ID', values: [vectorDbId] })
    }
  }

  const dynamicFiltersWithEnum = getFilterWithEnum(filters?.dynamicFilters ?? [])

  let filterString = ''
  filterString =
    filters && filters.filter
      ? parameterizeArrayofObjects([...filters.filter, ...(dynamicFiltersWithEnum ?? [])])
      : ''

  let paramStr = ''

  const booleanFilterString =
    filters && filters.booleanFilter ? parameterizeArrayofObjects(filters.booleanFilter) : ''

  const dateFilterString =
    filters && filters?.dateFilters && filters.dateFilters.length
      ? `dateFilters: ${parameterizeFilterArray(filters.dateFilters, ['key', 'start', 'end'])}`
      : ''

  const intRangeFilterString =
    filters && filters.intRangeFilters && filters.intRangeFilters.length
      ? `intRangeFilters: ${parameterizeFilterArray(filters.intRangeFilters, [
          'key',
          'min',
          'max'
        ])}`
      : ''

  if (filterString) {
    paramStr += `, filter: ${filterString}`
  }
  if (booleanFilterString) {
    paramStr += `, booleanFilter: ${booleanFilterString} `
  }
  if (dateFilterString) {
    paramStr += `, ${dateFilterString}`
  }
  if (intRangeFilterString) {
    paramStr += `, ${intRangeFilterString}`
  }

  if (paramStr) {
    paramStr = `(${paramStr})`
  }

  return gql`
    {
      objectGroupByVectorDbId${paramStr} {
        count
      }
    }
  `
}

export const mapQueryDuplicateFilesCount = (queryResponse: any): { total: number } => {
  try {
    const total = queryResponse?.objectGroupByVectorDbId?.count || 0
    return { total }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export interface DatasourceStatistic {
  datasourceId: string
  datasourceName: string
  numObjects: number
  duplicatedDataSize: number
  firstDocumentId: string
  firstDocumentCreatedAt: number
  lastDocumentId: string
  lastDocumentCreatedAt: number
  datasourceType: string
}

export interface DocumentChecksumGroupNode {
  id: string
  checksum: string
  groupName: string
  numObjects: number
  duplicatedDataSize: number
  duplicateOutsideDsCount: number
  firstDocumentId?: string
  firstDocumentCreatedAt?: string
  lastDocumentId?: string
  lastDocumentCreatedAt?: string
  fileSampleName: string
  fileType?: string
  numAttributes?: number
  class?: string
  size?: number
  sensitivity?: string
  datasourceStatistics: DatasourceStatistic[]
}

export interface DocumentChecksumGroupResponse {
  list: DocumentChecksumGroupNode[]
  count?: number
}

export const queryDocumentChecksumGroup = (params: {
  first?: number
  after?: string
  page?: number
  dataSourceId?: string
  vectorDbId?: string
  sortField?: string
  sortOrder?: number
}): string => {
  const {
    first = LIMIT_DEFAULT,
    after,
    page = 1,
    dataSourceId,
    vectorDbId,
    sortField,
    sortOrder
  } = params
  const cursor = after || getAfterCursor(page, first)
  const afterParam = cursor ? `, after: "${cursor}"` : ''
  const filterParts: string[] = []
  if (dataSourceId != null && dataSourceId !== '') {
    filterParts.push(`{ key: DATASOURCE_IDS, values: ["${dataSourceId}"] }`)
  }
  if (vectorDbId != null && vectorDbId !== '') {
    filterParts.push(`{ key: VECTOR_DB_ID, values: ["${vectorDbId}"] }`)
  }
  const filterParam = filterParts.length ? `, filter: [${filterParts.join(', ')}]` : ''
  const checksumGroupSortFieldMap: Record<string, string> = {
    numObjects: 'NUM_OBJECTS',
    duplicatedDataSize: 'DUPLICATED_DATA_SIZE'
  }
  const resolvedSortField = sortField ? checksumGroupSortFieldMap[sortField] || sortField : null
  const sortParam = resolvedSortField
    ? `, sortField: ${resolvedSortField}, sortByAsc: ${sortOrder === 1 ? 'true' : 'false'}`
    : ''

  return gql`
    {
      documentChecksumGroup(first: ${first}${afterParam}${filterParam}${sortParam}) {
        count
        edges {
          node {
            id
            checksum
            groupName
            numObjects
            duplicatedDataSize
            firstDocumentId
            firstDocumentCreatedAt
            lastDocumentId
            lastDocumentCreatedAt
            duplicateOutsideDsCount
            fileType
            fileSampleName
            numAttributes
            class
            size
            sensitivity
            datasourceStatistics {
              edges {
                node {
                  datasourceId
                  datasourceName
                  datasourceType
                  numObjects
                  duplicatedDataSize
                  firstDocumentId
                  firstDocumentCreatedAt
                  lastDocumentId
                  lastDocumentCreatedAt
                }
              }
            }
          }
        }
      }
    }
  `
}

export const mapQueryDocumentChecksumGroup = (
  queryResponse: any
): DocumentChecksumGroupResponse => {
  try {
    const list =
      queryResponse?.documentChecksumGroup?.edges?.map(({ node }) => ({
        id: node.id,
        checksum: node.checksum,
        groupName: node.groupName,
        numObjects: node.numObjects,
        duplicatedDataSize: node.duplicatedDataSize,
        duplicateOutsideDsCount: node.duplicateOutsideDsCount,
        firstDocumentId: node.firstDocumentId,
        firstDocumentCreatedAt: node.firstDocumentCreatedAt,
        lastDocumentId: node.lastDocumentId,
        lastDocumentCreatedAt: node.lastDocumentCreatedAt,
        fileType: node.fileType,
        numAttributes: node.numAttributes,
        class: node.class,
        size: node.size,
        sensitivity: node.sensitivity,
        fileSampleName: node.fileSampleName,
        datasourceStatistics:
          node.datasourceStatistics?.edges?.map(({ node: dsNode }) => ({
            datasourceId: dsNode.datasourceId,
            datasourceName: dsNode.datasourceName,
            numObjects: dsNode.numObjects,
            duplicatedDataSize: dsNode.duplicatedDataSize,
            firstDocumentId: dsNode.firstDocumentId,
            firstDocumentCreatedAt: dsNode.firstDocumentCreatedAt,
            lastDocumentId: dsNode.lastDocumentId,
            lastDocumentCreatedAt: dsNode.lastDocumentCreatedAt,
            datasourceType: dsNode.datasourceType
          })) || []
      })) || []

    const count = queryResponse?.documentChecksumGroup?.count || 0

    return { list, count }
  } catch (error) {
    console.error(error)
    throw error
  }
}
